import React, { createContext, useMemo, useEffect, useContext } from 'react';

import type { GetFeatures200 } from 'api/generated/schemas/getFeatures200';

import { useCurrentUser } from 'hooks/api/use-current-user';

import { useRemoteFeatureFlags } from 'hooks/use-remote-feature-flags';

import { DEFAULT_FEATURE_FLAGS } from './feature-flags.constants';
import type {
  FeatureFlags,
  FeatureFlagsContextValue,
} from './feature-flags.types';

export const FeatureFlagsContext = createContext<FeatureFlagsContextValue>({
  featureFlags: DEFAULT_FEATURE_FLAGS,
  featureFlagsLoaded: false,
});

export const useFeatureFlags = (): FeatureFlagsContextValue =>
  useContext(FeatureFlagsContext);

type FeatureFlagsProviderProps = {
  children: React.ReactNode;
};

const isFeatureFlagsPayload = (v: unknown): v is GetFeatures200 =>
  typeof v === 'object' && v !== null;

export const FeatureFlagsProvider: React.FC<FeatureFlagsProviderProps> = ({
  children,
}) => {
  const user = useCurrentUser();
  const featureFlagsQuery = useRemoteFeatureFlags(user?.data?.oktaUserId);

  const featureFlagsLoaded =
    featureFlagsQuery.isSuccess || featureFlagsQuery.isError;

  const featureFlags = useMemo<FeatureFlags>(() => {
    const raw = featureFlagsQuery.data;

    if (!isFeatureFlagsPayload(raw)) {
      return DEFAULT_FEATURE_FLAGS;
    }

    const data = raw;

    return {
      ...DEFAULT_FEATURE_FLAGS,
      articles: data['cms-articles'] || DEFAULT_FEATURE_FLAGS.articles,
      'tab-goals': data['tab-goals'] || DEFAULT_FEATURE_FLAGS['tab-goals'],
      'goals-monitoring':
        data['goals-monitoring'] || DEFAULT_FEATURE_FLAGS['goals-monitoring'],
      'critical-elements-monitoring':
        data['critical-elements-monitoring'] ||
        DEFAULT_FEATURE_FLAGS['critical-elements-monitoring'],
      'tab-insights':
        data['tab-insights'] || DEFAULT_FEATURE_FLAGS['tab-insights'],
      'tab-fao': data['tab-fao'] || DEFAULT_FEATURE_FLAGS['tab-fao'],
      'ai-assistant':
        data['ai-assistant'] || DEFAULT_FEATURE_FLAGS['ai-assistant'],
      'ai-token-usage':
        data['ai-token-usage'] || DEFAULT_FEATURE_FLAGS['ai-token-usage'],
      'ai-mark-monitoring':
        data['ai-mark-monitoring'] ||
        DEFAULT_FEATURE_FLAGS['ai-mark-monitoring'],
      'critical-populations':
        data['critical-populations'] ||
        DEFAULT_FEATURE_FLAGS['critical-populations'],
    };
  }, [featureFlagsQuery.data]);

  useEffect(() => {
    if (!featureFlagsQuery.isError) {
      return;
    }

    console.error('Failed to fetch feature flags:', featureFlagsQuery.error);
  }, [featureFlagsQuery.isError, featureFlagsQuery.error]);

  return (
    <FeatureFlagsContext.Provider value={{ featureFlags, featureFlagsLoaded }}>
      {children}
    </FeatureFlagsContext.Provider>
  );
};
